import React, { useState, useEffect } from 'react';
import styles from './Branding.module.css';
const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

const Branding = ({ appData, tenantPlan }) => {
  const [branding, setBranding] = useState({
    ai_bot_name: '',
    show_powered_by: true
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [originalBranding, setOriginalBranding] = useState({});
  const [previewMessage, setPreviewMessage] = useState('');

  useEffect(() => {
    if (appData?.branding) {
      setBranding({
        ai_bot_name: appData.branding.ai_bot_name || '',
        show_powered_by: appData.branding.show_powered_by !== undefined ? appData.branding.show_powered_by : true
      });
      setOriginalBranding({
        ai_bot_name: appData.branding.ai_bot_name || '',
        show_powered_by: appData.branding.show_powered_by !== undefined ? appData.branding.show_powered_by : true
      });
    }
  }, [appData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBranding(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const hasChanges = () => {
    return branding.ai_bot_name !== originalBranding.ai_bot_name ||
      branding.show_powered_by !== originalBranding.show_powered_by;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hasChanges()) {
      setMessage({ type: 'info', text: 'No changes to save' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(
        `https://us.apps2.${backend_api_base_url}/update_branding?app_name=${appData.app_name}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': localStorage.getItem('token') || ''
          },
          body: JSON.stringify({
            branding: {
              ai_bot_name: branding.ai_bot_name,
              show_powered_by: branding.show_powered_by
            }
          })
        }
      );

      const result = await response.json();

      if (result.statusCode === 200) {
        const updatedBranding = JSON.parse(result.body).branding;
        setOriginalBranding({
          ai_bot_name: updatedBranding.ai_bot_name || '',
          show_powered_by: updatedBranding.show_powered_by !== undefined ? updatedBranding.show_powered_by : true
        });
        setMessage({ type: 'success', text: 'Branding updated successfully!' });

        if (window.updateAppData) {
          window.updateAppData({
            ...appData,
            branding: updatedBranding
          });
        }
      } else {
        throw new Error('Failed to update branding');
      }
    } catch (error) {
      console.error('Error updating branding:', error);
      setMessage({ type: 'error', text: 'Failed to update branding. Please try again.' });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleReset = () => {
    setBranding({
      ai_bot_name: originalBranding.ai_bot_name,
      show_powered_by: originalBranding.show_powered_by
    });
    setMessage({ type: 'info', text: 'Changes reset' });
    setTimeout(() => setMessage({ type: '', text: '' }), 2000);
  };

  const handleSendPreviewMessage = () => {
    if (previewMessage.trim()) {
      // Just for demo - in real chat this would send to bot
      setPreviewMessage('');
    }
  };

  // Check if user has access to branding features based on tenant plan
  const hasBrandingAccess = () => {
    // If tenantPlan is BASIC, restrict access
    if (tenantPlan === 'BASIC') {
      return false;
    }
    // Allow for PRO, ENTERPRISE, or any other plan
    return true;
  };

  // If no access, show upgrade message
  if (!hasBrandingAccess()) {
    return (
      <div className={styles.noAccessContainer}>
        <div className={styles.noAccessCard}>
          <div className={styles.noAccessIcon}>🔒</div>
          <h3 className={styles.noAccessTitle}>Feature Restricted</h3>
          <p className={styles.noAccessMessage}>
            Branding customization is only available on Pro and Enterprise plans.
          </p>
          <p className={styles.noAccessSubmessage}>
            Upgrade your plan to customize your AI assistant's appearance and behavior.
          </p>
          <button
            className={styles.upgradeButton}
            onClick={() => window.open('https://sageion.com/pricing', '_blank')}
          >
            View Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Branding Settings</h2>
        <p className={styles.subtitle}>Customize your AI assistant's appearance and behavior</p>
      </div>

      {message.text && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>AI Chat Configuration</h3>

          <div className={styles.formGroup}>
            <label htmlFor="ai_bot_name" className={styles.label}>
              AI Chat Name
              <span className={styles.optional}>(Optional)</span>
            </label>
            <input
              type="text"
              id="ai_bot_name"
              name="ai_bot_name"
              value={branding.ai_bot_name}
              onChange={handleChange}
              placeholder="e.g., Assistant, Support Bot, Helper"
              className={styles.input}
              maxLength={50}
            />
            <p className={styles.helperText}>
              This name will appear as the bot's display name in chat conversations.
            </p>
          </div>
        </div>

        <div className={styles.previewSection}>
          <div className={styles.previewHeader}>
            <h3 className={styles.sectionTitle}>Live Preview</h3>
            <label className={styles.toggleSwitch}>
              <input
                type="checkbox"
                name="show_powered_by"
                checked={branding.show_powered_by}
                onChange={handleChange}
              />
              <span className={styles.toggleSlider}></span>
              <span className={styles.toggleLabel}>Show "Powered by"</span>
            </label>
          </div>

          <div className={styles.realChatPreview}>
            {/* Chat Header */}
            <div className={styles.chatHeader}>
              <div className={styles.chatHeaderInfo}>
                <div className={styles.botAvatarLarge}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 4c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm0 13c-2.33 0-4.31-1.46-5.11-3.5h10.22c-.8 2.04-2.78 3.5-5.11 3.5z" fill="currentColor" />
                  </svg>
                </div>
                <div className={styles.chatHeaderText}>
                  <div className={styles.chatBotName}>
                    {branding.ai_bot_name || 'AI Assistant'}
                  </div>
                  <div className={styles.chatStatus}>Active now</div>
                </div>
              </div>
              <div className={styles.chatHeaderActions}>
                <button type="button" className={styles.iconButton}>⋯</button>
              </div>
            </div>

            {/* Chat Messages Area */}
            <div className={styles.chatMessages}>
              <div className={styles.messageGroup}>
                <div className={styles.botMessageBubble}>
                  <div className={styles.botMessageContent}>
                    Hello! How can I help you today?
                  </div>
                  <div className={styles.messageTime}>Just now</div>
                </div>
              </div>

              <div className={styles.messageGroup}>
                <div className={styles.userMessageBubble}>
                  <div className={styles.userMessageContent}>
                    Can you help me with my account?
                  </div>
                  <div className={styles.messageTime}>Just now</div>
                </div>
              </div>

              <div className={styles.messageGroup}>
                <div className={styles.botMessageBubble}>
                  <div className={styles.botMessageContent}>
                    Of course! I'd be happy to help you with your account. What specific issue are you experiencing?
                  </div>
                  <div className={styles.messageTime}>Just now</div>
                </div>
              </div>
            </div>


            {/* Chat Input Area */}
            <div className={styles.chatInput}>
              <input
                type="text"
                value={previewMessage}
                onChange={(e) => setPreviewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendPreviewMessage()}
                placeholder="Type a message here for bot..."
                className={styles.chatInputField}
              />
              <button
                type="button"
                onClick={handleSendPreviewMessage}
                className={styles.sendButton}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" />
                </svg>
              </button>
            </div>

            {/* Footer - Conditionally render based on show_powered_by state */}
            {branding.show_powered_by && (
              <div className={styles.chatFooter}>
                Powered by{' '}
                <a
                  href="https://sageion.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.magicChatLink}
                >
                  Sageion
                </a>
              </div>
            )}
          </div>
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            onClick={handleReset}
            className={styles.resetButton}
            disabled={loading || !hasChanges()}
          >
            Reset
          </button>
          <button
            type="submit"
            className={styles.saveButton}
            disabled={loading || !hasChanges()}
          >
            {loading ? (
              <>
                <span className={styles.spinner}></span>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Branding;